import { Component, ComponentChild, ComponentFactory, FunctionalComponent, h } from 'preact';
import { Connect } from 'redux-zero/preact';
import { bindActions } from 'redux-zero/utils';
import { withText } from 'preact-i18n';
import { Properties } from '../common';
import { isFunction, isPresent, isServer } from '../utils';
import { ActionsFactory, StoreContext } from './types';
import { CssModule, withStyles } from '../style';
import { KeysOfLocalizations } from '../i18n';

// eslint-disable-next-line @typescript-eslint/ban-types
export interface ConnectStore<TState extends object, TMapped, TActions> {
  mapStateToProps?: (state: TState) => TMapped;
  actions?: ActionsFactory<TState, TActions>;
}

export function connectStore<
  TProps extends Properties,
  TStoreState extends object, // eslint-disable-line @typescript-eslint/ban-types
  TMapped,
  TActions
>({ mapStateToProps, actions }: ConnectStore<TStoreState, TMapped, TActions>): any {
  return function connectStoreWrapper(ComposedComponent: ComponentFactory<TProps>): any {
    // eslint-disable-next-line react/prefer-stateless-function
    return class ConnectStore extends Component<TProps> {
      render(props: TProps, _state: never, { store }: StoreContext<TStoreState>): ComponentChild {
        if (isServer()) {
          const mappedProps = isFunction(mapStateToProps) ? mapStateToProps(store.getState()) : {};
          const boundActions = isPresent(actions) ? bindActions(actions, store) : {};
          return <ComposedComponent {...props} {...mappedProps} {...boundActions} />;
        }
        return (
          <Connect mapToProps={mapStateToProps} actions={actions}>
            {(mappedProps: TMapped): ComponentChild => <ComposedComponent {...props} {...mappedProps} />}
          </Connect>
        );
      }
    };
  };
}

// eslint-disable-next-line @typescript-eslint/ban-types
export const connectWithStyles = <TComponent, TStoreState extends object = {}, TMapped = void, TActions = void>(
  styleProps: CssModule[],
  storeProps: ConnectStore<TStoreState, TMapped, TActions>
) => {
  return (component: FunctionalComponent<TComponent>): FunctionalComponent<TComponent> => {
    return withStyles(...styleProps)(connectStore(storeProps)(component));
  };
};

// eslint-disable-next-line @typescript-eslint/ban-types
export const connectWithI18nAndStyles = <TComponent, TStoreState extends object = {}, TMapped = void, TActions = void>(
  styleProps: CssModule[],
  storeProps: ConnectStore<TStoreState, TMapped, TActions>,
  translationKey: Record<string, KeysOfLocalizations>
) => {
  return (component: FunctionalComponent<TComponent>): FunctionalComponent<TComponent> => {
    return withText<string>(translationKey)(
      withStyles(...styleProps)(connectStore(storeProps)(component))
    ) as unknown as FunctionalComponent<TComponent>;
  };
};
